/**
 * PrimaryKeyAddFormBody — name + columns form for `add-primary-key` plus the
 * per-engine extra fields the synthesizer accepts (mssql clustered, postgres
 * INCLUDE, oracle tablespace, postgres+oracle deferrable). MySQL has no
 * per-engine extras at the constraint level; an info note explains that
 * AUTO_INCREMENT-on-PK is a column-level concern set on the column itself.
 *
 * Engine-aware behaviour:
 *   - sqlite: caller (`PrimaryKeyAddPopover`) gates rendering with
 *     `isAddPrimaryKeySupported`; included as a defensive runtime guard.
 *   - mssql: clustered radio enabled. Other engines: clustered absent.
 *   - postgres: INCLUDE input + DEFERRABLE radios enabled.
 *   - oracle: tablespace input + DEFERRABLE radios enabled.
 *   - mysql: only columns + name.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B1.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import {
  ambiguousColumnNameOf,
  isAmbiguousColumnNameError,
} from '../../../../sql-resolver/ddl/apply/columnEdit.alterTable';
import type { PrimaryKeyDef } from '../../../../sql-resolver/types/ddlEdit';
import {
  commitAddPrimaryKey,
  isAutoIncrementOnPrimaryKey,
  isClusteredSupported,
  isDeferrableSupported,
  isIncludeColumnsSupported,
  isTablespaceSupported,
  parseColumnList,
  validatePrimaryKeyForEngine,
} from '../../edit/edits/addPrimaryKey';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ambiguousColumnMessage } from './ambiguousColumnCopy';
import { FormEngineChip } from './FormEngineChip';

type ClusteredMode = 'default' | 'clustered' | 'nonclustered';
type DeferrableMode = 'unspecified' | 'deferred' | 'immediate';

export interface PrimaryKeyAddFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function PrimaryKeyAddFormBody(props: PrimaryKeyAddFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const showClustered = isClusteredSupported(engine);
  const showInclude = isIncludeColumnsSupported(engine);
  const showTablespace = isTablespaceSupported(engine);
  const showDeferrable = isDeferrableSupported(engine);
  const showAutoIncrementNote = isAutoIncrementOnPrimaryKey(engine);

  const [columnsInput, setColumnsInput] = useState('');
  const [name, setName] = useState('');
  const [clustered, setClustered] = useState<ClusteredMode>('default');
  const [include, setInclude] = useState('');
  const [tablespace, setTablespace] = useState('');
  const [deferrable, setDeferrable] = useState<DeferrableMode>('unspecified');
  const [error, setError] = useState<string | null>(null);
  /** The thrown error's own text, kept when the box shows a rewritten
   *  sentence — a developer reading the popover still gets the precise one. */
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildPrimaryKey(): PrimaryKeyDef | { error: string } {
    const columns = parseColumnList(columnsInput);
    const draft: {
      name?: string;
      columns: readonly string[];
      clustered?: 'clustered' | 'nonclustered';
      includeColumns?: readonly string[];
      tablespace?: string;
      deferrable?: 'deferred' | 'immediate';
    } = { columns };
    const trimmedName = name.trim();
    if (trimmedName !== '') draft.name = trimmedName;
    if (showClustered && clustered !== 'default') draft.clustered = clustered;
    if (showInclude) {
      const includeCols = parseColumnList(include);
      if (includeCols.length > 0) draft.includeColumns = includeCols;
    }
    if (showTablespace) {
      const ts = tablespace.trim();
      if (ts !== '') draft.tablespace = ts;
    }
    if (showDeferrable && deferrable !== 'unspecified') {
      draft.deferrable = deferrable;
    }
    const validationError = validatePrimaryKeyForEngine(engine, draft);
    if (validationError !== null) return { error: validationError };
    return draft;
  }

  async function handleSubmit() {
    setError(null);
    setErrorDetail(null);
    const built = buildPrimaryKey();
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setPending(true);
    try {
      await commitAddPrimaryKey(editContext, { primaryKey: built });
      onCommitted?.();
    } catch (err) {
      const ambiguous = ambiguousColumnNameOf(err);
      if (isAmbiguousColumnNameError(err) && ambiguous !== null) {
        setError(ambiguousColumnMessage(ambiguous, engine, 'type-delimiters'));
        setErrorDetail(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Add primary key</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-columns`} className="text-xs">
          Columns <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-columns`}
          data-testid={`${testId}-columns`}
          value={columnsInput}
          onChange={(e) => setColumnsInput(e.target.value)}
          placeholder="id, version"
          className="h-8 font-mono text-xs"
        />
        <span className="text-[10px] text-muted-foreground">Comma-separated column list.</span>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">Name (optional)</Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="pk_table"
          className="h-8 font-mono text-xs"
        />
      </div>

      {showClustered ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs">Clustering</legend>
          <RadioGroup
            value={clustered}
            onValueChange={(v) => setClustered(v as ClusteredMode)}
            className="flex items-center gap-3 text-xs"
          >
            {(['default', 'clustered', 'nonclustered'] as ClusteredMode[]).map((mode) => (
              <label key={mode} className="flex items-center gap-1">
                <RadioGroupItem
                  value={mode}
                  data-testid={`${testId}-clustered-${mode}`}
                />
                <span className="capitalize">{mode === 'default' ? 'Engine default' : mode}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      ) : null}

      {showInclude ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-include`} className="text-xs" title="Requires PostgreSQL 11+">INCLUDE columns</Label>
          <Input
            id={`${testId}-include`}
            data-testid={`${testId}-include`}
            value={include}
            onChange={(e) => setInclude(e.target.value)}
            placeholder="note, status"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {showTablespace ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-tablespace`} className="text-xs">USING INDEX TABLESPACE</Label>
          <Input
            id={`${testId}-tablespace`}
            data-testid={`${testId}-tablespace`}
            value={tablespace}
            onChange={(e) => setTablespace(e.target.value)}
            placeholder="TS_INDEX"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {showDeferrable ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs">Deferrable</legend>
          <RadioGroup
            value={deferrable}
            onValueChange={(v) => setDeferrable(v as DeferrableMode)}
            className="flex items-center gap-3 text-xs"
          >
            {(['unspecified', 'deferred', 'immediate'] as DeferrableMode[]).map((mode) => (
              <label key={mode} className="flex items-center gap-1">
                <RadioGroupItem
                  value={mode}
                  data-testid={`${testId}-deferrable-${mode}`}
                />
                <span className="capitalize">{mode === 'unspecified' ? 'Engine default' : mode}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      ) : null}

      {showAutoIncrementNote ? (
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-auto-increment-note`}
        >
          MySQL: AUTO_INCREMENT must be set on the column itself, not on the constraint.
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
          title={errorDetail ?? undefined}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
